import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { Consultation, ConsultationStatus } from '@/core/models/consultation.model'
import { useReportLicenseEnabled } from '@/features/report'
import ContentHeader from '@/pages/diagnostic/components/ContentHeader'

import { formatElapsed, speakerNames } from '../model/transcript'
import { useLiveTranscription } from '../model/useLiveTranscription'
import { useTranscriptionConsent } from '../model/useTranscriptionConsent'
import { StructuredDataReviewDialog } from './StructuredDataReviewDialog'
import { TranscriptionConsentPanel } from './TranscriptionConsentPanel'
import { TranscriptList } from './TranscriptList'

export type LiveTranscriptionSectionProps = {
  consultation: Consultation
  consultationId: string
  /** In-person consultation: the patient is in the room, so consent is only given verbally. */
  inPerson?: boolean
}

/** Server refusals (see `LiveTranscriptionService` / `ReportsLicenseService`), shown as readable text. */
const SERVER_ERROR_KEYS: Record<string, string> = {
  PATIENT_CONSENT_REQUIRED: 'page.liveConsultation.transcription.consent.required',
  REPORTS_LICENSE_REQUIRED: 'page.liveConsultation.transcription.licenseRequired',
}

/**
 * The "Transcription" sidebar section of a live consultation: one capture toggle, the
 * live speaker-labelled transcript, and "Add to structured data" which opens ELARA's
 * proposals for review.
 */
export function LiveTranscriptionSection({ consultation, consultationId, inPerson = false }: LiveTranscriptionSectionProps) {
  const { t, i18n } = useTranslation()
  const hasLicense = useReportLicenseEnabled()
  const live = useLiveTranscription({ consultationId, language: i18n.language?.split('-')[0] })
  const consent = useTranscriptionConsent({ consultationId, onGranted: live.start })
  const [reviewOpen, setReviewOpen] = useState(false)
  const names = useMemo(() => speakerNames(consultation), [consultation])

  const isActive = consultation.status === ConsultationStatus.Active
  const isListening = live.status === 'listening'
  const isBusy = live.status === 'connecting' || live.status === 'stopping'
  const canReview = !!live.transcriptionId && live.segments.length > 0 && !isBusy
  const needsConsent = isActive && hasLicense && consent.status !== 'granted' && !isListening
  const errorMessage = live.error && SERVER_ERROR_KEYS[live.error] ? t(SERVER_ERROR_KEYS[live.error]) : live.error

  return (
    <Stack sx={{ height: '100%', p: '20px 16px', boxSizing: 'border-box' }} spacing={2} data-testid="live-transcription-section">
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} flexWrap="wrap" useFlexGap>
        <ContentHeader title={t('page.liveConsultation.transcription.title')} subtitle={t('page.liveConsultation.transcription.subtitle')} />
        <Stack direction="row" spacing={1} alignItems="center">
          {isListening && (
            <Chip
              size="small"
              color="error"
              icon={<MicIcon />}
              label={`${t('page.liveConsultation.transcription.status.listening')} · ${formatElapsed(live.elapsedSeconds)}`}
              sx={{ '& .MuiChip-icon': { animation: 'live-transcription-pulse 1.4s infinite' }, '@keyframes live-transcription-pulse': { '50%': { opacity: 0.3 } } }}
            />
          )}
          {isActive && hasLicense && !needsConsent && (
            <Tooltip title={isListening ? '' : t('page.liveConsultation.transcription.startHint')}>
              <span>
                <Button
                  variant={isListening ? 'outlined' : 'contained'}
                  color={isListening ? 'error' : 'primary'}
                  startIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : isListening ? <StopIcon /> : <MicIcon />}
                  disabled={isBusy}
                  onClick={isListening ? live.stop : live.start}
                  data-testid="live-transcription-toggle"
                >
                  {isListening ? t('page.liveConsultation.transcription.stop') : t('page.liveConsultation.transcription.start')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {!hasLicense && <Alert severity="warning">{t('page.liveConsultation.transcription.licenseRequired')}</Alert>}
      {needsConsent && (
        <TranscriptionConsentPanel
          status={consent.status}
          inPerson={inPerson}
          busy={consent.isRecording || isBusy}
          onRequestFromPatient={consent.requestFromPatient}
          onVerbalAnswer={consent.recordVerbalAnswer}
        />
      )}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      {isListening && (
        <Typography variant="caption" sx={{ color: colors.gray500 }}>
          {live.remoteCaptured ? t('page.liveConsultation.transcription.remoteCaptured') : t('page.liveConsultation.transcription.remoteMissing')}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
        {live.isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <TranscriptList
            segments={live.segments}
            interim={live.interim}
            activity={live.activity}
            names={names}
            emptyHint={t(isListening ? 'page.liveConsultation.transcription.listeningEmpty' : 'page.liveConsultation.transcription.empty')}
          />
        )}
      </Box>

      <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1, borderTop: `1px solid ${colors.gray200 ?? '#e5e7eb'}` }}>
        <Button variant="contained" startIcon={<AutoAwesomeIcon />} disabled={!canReview} onClick={() => setReviewOpen(true)} data-testid="add-to-structured-data">
          {t('page.liveConsultation.transcription.addToStructuredData')}
        </Button>
      </Stack>

      <StructuredDataReviewDialog open={reviewOpen} transcriptionId={live.transcriptionId} consultationId={consultationId} onClose={() => setReviewOpen(false)} />
    </Stack>
  )
}

export default LiveTranscriptionSection
